import {keepPreviousData, queryOptions} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import {repositorySavedViewEndpoints} from './contexts/SavedViewEndpointsContext'
import type {SavedView, SavedViewId, SavedViewsResponse} from './types'

export const SAVED_VIEWS_QUERY_KEY = 'repo-saved-views'
export const SAVED_VIEW_QUERY_KEY = 'repo-saved-view'
export const SAVED_VIEW_PINS_QUERY_KEY = 'saved-view-pins'

export class SavedViewFetchError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'SavedViewFetchError'
    this.status = status
  }
}

export async function parseFetchErrorMessage(response: Response, fallback: string): Promise<string> {
  return (await parseFetchError(response, fallback)).message
}

export interface ParsedFetchError {
  message: string
  code?: string
}

type FetchSavedViewsTarget =
  | {
      owner: string
      repo: string
      listUrl?: never
    }
  | {
      owner?: never
      repo?: never
      listUrl: string
    }

type FetchSavedViewsOptions = FetchSavedViewsTarget & {
  // Defaults to the repository list endpoint built from `owner`/`repo`. Account
  // level surfaces are not under a repository, so they pass a URL instead.
  signal?: AbortSignal
}

export async function fetchSavedViews({owner, repo, listUrl, signal}: FetchSavedViewsOptions) {
  const response = await verifiedFetchJSON(listUrl ?? repositorySavedViewEndpoints(owner, repo).listUrl, {signal})
  if (!response.ok) {
    throw new SavedViewFetchError(
      response.status,
      await parseFetchErrorMessage(response, `Failed to fetch saved views (${response.status})`),
    )
  }

  return (await response.json()) as SavedViewsResponse
}

export function savedViewsQueryKey({
  owner,
  repo,
  listUrl,
}: {
  owner: string
  repo: string
  listUrl?: string
}): readonly unknown[] {
  const repositoryListUrl = repositorySavedViewEndpoints(owner, repo).listUrl
  return listUrl === undefined || listUrl === repositoryListUrl
    ? [SAVED_VIEWS_QUERY_KEY, owner, repo]
    : [SAVED_VIEWS_QUERY_KEY, owner, repo, listUrl]
}

export function savedViewQueryKey({
  owner,
  repo,
  basePath,
  id,
}: {
  owner: string
  repo: string
  basePath?: string
  id: SavedViewId
}): readonly unknown[] {
  const repositoryBasePath = repositorySavedViewEndpoints(owner, repo).basePath
  return basePath === undefined || basePath === repositoryBasePath
    ? [SAVED_VIEW_QUERY_KEY, owner, repo, String(id)]
    : [SAVED_VIEW_QUERY_KEY, owner, repo, basePath, String(id)]
}

export function savedViewPinsQueryKey({
  owner,
  repo,
  listUrl,
}: {
  owner: string
  repo: string
  listUrl: string
}): readonly unknown[] {
  return [SAVED_VIEW_PINS_QUERY_KEY, owner, repo, listUrl]
}

export async function parseFetchError(response: Response, fallback: string): Promise<ParsedFetchError> {
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    try {
      const payload = (await response.json()) as {error?: {message?: string; code?: string}} | null
      if (payload?.error?.message) {
        return {message: payload.error.message, code: payload.error.code}
      }
    } catch {
      return {message: fallback}
    }
    return {message: fallback}
  }

  try {
    const text = await response.text()
    if (text) return {message: text}
  } catch {
    return {message: fallback}
  }
  return {message: fallback}
}

export function savedViewsQueryOptions({
  owner,
  repo,
  listUrl,
  initialData,
}: {
  owner: string
  repo: string
  listUrl?: string
  initialData?: SavedViewsResponse
}) {
  const resolvedListUrl = listUrl ?? repositorySavedViewEndpoints(owner, repo).listUrl
  return queryOptions({
    queryKey: savedViewsQueryKey({owner, repo, listUrl: resolvedListUrl}),
    placeholderData: keepPreviousData,
    initialData,
    staleTime: initialData ? Infinity : 0,
    queryFn: ({signal}) => fetchSavedViews({listUrl: resolvedListUrl, signal}),
  })
}

/** One view and whether the viewer may manage the views beside it, as `/:id.json` answers. */
export interface SavedViewResponse {
  savedView: SavedView
  viewerCanManageViews: boolean
}

export function savedViewQueryOptions({
  owner,
  repo,
  basePath,
  id,
  initialData,
  initialDataUpdatedAt,
}: {
  owner: string
  repo: string
  basePath?: string
  id: SavedViewId
  /** The view the page was served with, so the page renders before `/:id.json` answers. */
  initialData?: SavedViewResponse
  /** When `initialData` was fetched. */
  initialDataUpdatedAt?: number
}) {
  const resolvedBasePath = basePath ?? repositorySavedViewEndpoints(owner, repo).basePath
  return queryOptions({
    queryKey: savedViewQueryKey({owner, repo, basePath: resolvedBasePath, id}),
    initialData,
    initialDataUpdatedAt,
    queryFn: async ({signal}) => {
      const response = await verifiedFetchJSON(`${resolvedBasePath}/${encodeURIComponent(id)}.json`, {signal})
      if (!response.ok) {
        throw new SavedViewFetchError(
          response.status,
          await parseFetchErrorMessage(response, `Failed to fetch view (${response.status})`),
        )
      }
      return (await response.json()) as SavedViewResponse
    },
  })
}
